import React, { useEffect, useState } from 'react';
import {
  isUndefinedNullEmpty,
  SbCurrentUser,
} from '../submission_boards/component/SbModel';
import {
  ArrowDownToLineIcon,
  FileTextIcon,
  LayoutGridIcon,
  Maximize2Icon,
  Minimize2Icon,
  TextIcon,
} from 'lucide-react';
import { useIsMobile } from '../submission_boards/component/useIsMobile';
import FilePreviewer from '../submission_kits/components/FilePreviewer';

interface WorkKitProps {
  currentUser: SbCurrentUser;
  user: any;
  from: any;
  showProfileHeader: (show:boolean) => void;
}
// component to show profile photo/age/weight/height
// used by SbDetails and SbDetailsRecipient
export const WorkKit: React.FC<WorkKitProps> = ({ currentUser, user, from, showProfileHeader }) => {
  const isMobile = useIsMobile();
  const [gridLayout, setgridLayout] = useState(false);
  const [kitDocuments, setKitDocuments] = useState(user?.work_kit?.kit_documents || []);
  const [showTopProfileHeader, setShowTopProfileHeader] = useState(true);

  useEffect(() => {
    console.log(kitDocuments);
    showProfileHeader(showTopProfileHeader);
  }, [showTopProfileHeader]);

  const canDownloadDocuments = () => {
    if (from === 'public') return false;

    // Super admin can always download
    if (currentUser?.super_admin) return true;

    // Check if user has Pro or higher subscription tier
    const allowedTiers = ['pro', 'pro_idle', 'premium', 'enterprise'];
    const isPro = currentUser?.subscription_tier && allowedTiers.includes(currentUser.subscription_tier);
    
    return currentUser?.id && isPro;
  };

  const renderImage = (mainKitDoc:any, index: number) => {
    const isEmpty = !mainKitDoc;
    const fileContentType = mainKitDoc?.file_content_type || 'application/pdf';
    const isPdf = fileContentType === 'application/pdf' || fileContentType?.includes('pdf');
    
    return (
      <>
        {isEmpty ? (
          <></>
        ) : (
          <li key={index} 
           style={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem',
            gap: '1rem',
            width: '18.75rem',
            height: '5.75rem',
            background: '#E9EEF6',
            border: '0.0625rem solid rgba(140, 162, 194, 0.5)',
            borderRadius: '1rem',
            flex: 'none',
            flexGrow: 0
          }}>
            <div className='flex flex-row items-center justify-start flex-1 min-w-0 h-full gap-4'>
              <div className='flex justify-center items-center flex-shrink-0' style={{ width: '3.75rem', height: '3.75rem', borderRadius: '0.5rem', overflow: 'hidden', backgroundColor: '#fff' }}>
                {isPdf ? (
                  <>{mainKitDoc.pdf_preview_variant ? <>
                    <img className="h-full w-full object-cover" src={mainKitDoc.pdf_preview_variant}>
                    </img> </> : <object
                    data={`${mainKitDoc.file_url}#page=1&zoom=50&view=FitH&scrollbar=0`}
                    type="application/pdf"
                    className="w-full h-full"
                    style={{ pointerEvents: 'none' }}
                  >
                    <FilePreviewer
                      file_url={mainKitDoc.file_url || ''}
                      file_name={mainKitDoc.file_name || ''}
                      file_content_type={fileContentType}
                      object_fit="object-cover"
                      dynamicHeight={false}
                    />
                  </object>}
                  </>
                ) : (
                  <FilePreviewer
                    file_url={mainKitDoc.file_url || ''}
                    file_name={mainKitDoc.file_name || ''}
                    file_content_type={fileContentType}
                    object_fit="object-cover"
                    dynamicHeight={false}
                  />
                )}
              </div>
              <h3 className="ml-0 text-gray-900 text-base truncate leading-6 font-normal flex-1 min-w-0 whitespace-normal" 
              style={{ marginLeft: '0px',
              display: '-webkit-box', 
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
               wordBreak: 'break-all'
               }}>
                {mainKitDoc.file_name}</h3>
            </div>
            <div className="flex flex-row items-center flex-shrink-0">
              {canDownloadDocuments() && (
                <a  className="h-12 w-12 bg-white rounded-full flex justify-center items-center text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500 shadow-sm"
                    href={mainKitDoc.file_url} target='_blank'>
                  <ArrowDownToLineIcon className="h-5 w-5"/>
                </a>
              )}
            </div>
          </li>
        )}
      </>
    );
  };

  const gridBoxItem = (title, value, icon: React.ReactNode) => {
    return (
      <div
        className="flex flex-col justify-center items-center gap-2-1 border border-blue-502 "
        style={{
          padding: '0.375rem',
          borderRadius: '0.625rem',
          color: '#8CA2C280',
          backgroundColor: '#E9EEF6D9',
        }}
      >
        {title != null && (
          <div
            className="flex flex-col justify-end items-center gap-2-1 bg-white w-full"
            style={{
              padding: '0.75rem 0.5rem 0.375rem',
              borderRadius: '0.375rem 0.375rem 0.125rem 0.125rem',
            }}
          >
            <div className="font-normal font-semibold text-13 text-center text-blue-403">
              {title}
            </div>
          </div>
        )}
        <div className="flex flex-col justify--center items-start gap-2-1">
          <div
            className=" flex flex-row justify--center items-center gap-2-1 px-5 py-2-1 text-lg primary-color"
            style={{
              minHeight: '1.875rem',
            }}
          >
            {icon != null && <div id="icon">{icon}</div>}
            <div>{value}</div>
          </div>
        </div>
      </div>
    );
  };

  const listBoxItem = (title, value) => {
    return (
      <div className="flex flex-col items-start p-0">
        {title != null && (
          <div
            className="flex flex-row  gap-2-1 w-full"
            style={{
              paddingTop: '0.1875rem',
            }}
          >
            <div className="text-13 font-medium text-left text-blue-403 leading-5"
             style={{ lineHeight: '20px' }}>
              {title}</div>
          </div>
        )}
        <div className="flex flex-col primary-color font-regular text-lg">{value}</div>
      </div>
    );
  };

  // const toggleRender = () => {
  //   return (
  //     <div
  //       className="flex flex-row cursor-pointer items-center gap-1-1 rounded-full"
  //       style={{
  //         padding: '1rem 0.5rem 1rem 0.5rem',
  //         backgroundColor: '#A5B7D380',
  //         maxHeight: '1.625rem',
  //       }}
  //       onClick={() => {
  //         setgridLayout(!gridLayout);
  //       }}
  //     >
  //       <div
  //         className={
  //           'flex flex-col items-center gap-2 rounded-full ' + (gridLayout ? 'bg-white' : '')
  //         }
  //         style={{
  //           padding: '0.3125rem 1.125rem 0.3125rem 0.625rem',
  //           // margin: '0rem -0.625rem',
  //           zIndex: 1,
  //         }}
  //       >
  //         <LayoutGridIcon className="w-4 h-4" />
  //       </div>

  //       <div
  //         className={
  //           'flex flex-col items-center gap-2 rounded-full ' + (!gridLayout ? 'bg-white' : '')
  //         }
  //         style={{
  //           padding: '0.3125rem 0.875rem 0.3125rem 0.875rem',
  //           zIndex: 1,
  //         }}
  //       >
  //         <TextIcon className="w-4 h-4" />
  //       </div>
  //     </div>
  //   );
  // };

  const getAddress = (address:any) => {
    if(isUndefinedNullEmpty(address.street_address)
      && isUndefinedNullEmpty(address.street_address_line2)
      && isUndefinedNullEmpty(address.city)
      && isUndefinedNullEmpty(address.state)
      && isUndefinedNullEmpty(address.country)
      && isUndefinedNullEmpty(address.postal_code)
    )
    {
      return <>-</>
    }
 
    return <>
    {address.street_address && <>{address.street_address}<br/></>}
    {address.street_address_line2 && <>{address.street_address_line2}<br/></>}
    {address.city && <>{address.city}, </>}
    {address.state && <>{address.state}, </>}
    {address.country && <>{address.country}<br/></>}
    {address.postal_code && <>{address.postal_code}<br/></>}
    </>;
  }
  return (
    <>
      <div
        className="flex flex-col bg-white rounded-2xl border border-blue-502 "
         style={{
          paddingTop: isMobile?'2.5rem':'2.5rem', // 40px 60px
          paddingBottom: isMobile?'2.5rem':'2.5rem', // 60px
          paddingLeft: isMobile?'1.25rem':'3.75rem', // 20px 60px
          paddingRight: isMobile?'1.25rem':'3.75rem', // 60px
          gap: isMobile?'1rem':'3.75rem', // 60px
        }}
      >
        <div className="flex flex-col sm:flex-row flex-1 justify-between items-center gap-4 border-b border-blue-502 pb-2">
          <div className="flex flex-row flex-1 items-center">
            <div className={`w-full flex-1 m-0 ${isMobile ? 'h-auto text-3xl leading-9' : 'h-12 text-3xl leading-12'} font-medium tracking-tight`} style={{ color: '#576C89' }}>
            Work Kit</div>
          </div>
          {/* icons */}
          {showTopProfileHeader? <div
            className="w-7 h-7 cursor-pointer  hidden sm:flex items-center justify-center overflow-hidden"
            style={{
              color: '#576C89',
            }}
            onClick={() => {
              setShowTopProfileHeader(false);
            }}
          >
            <Maximize2Icon className="w-7 h-7" />
          </div>:<div
            className="w-7 h-7 cursor-pointer  hidden sm:flex items-center justify-center overflow-hidden"
            style={{
              color: '#576C89',
            }}
            onClick={() => {
              setShowTopProfileHeader(true);
            }}
          >
            <Minimize2Icon className="w-7 h-7" />
          </div>}
        </div>

        <div className="flex flex-col sm:flex-row gap-10">
          {/* Left Section */}
          <div className="gap-5 flex flex-col flex-1">

            <div className="flex flex-col sm:flex-row gap-5 pb-7 border-b border-blue-502 " flex-row="">
              <h3 className="text-xl font-semibold mb-2" style={{ width: '10.625rem' }}>
                Personal Information
              </h3>
              {!gridLayout && (
                <div className="flex flex-col gap-2-1 ">
                  {listBoxItem('Full Name', user.work_kit.legal_name)}
                  {listBoxItem('Address', getAddress(user))}
                  {listBoxItem('Birthday', isUndefinedNullEmpty(user.birthdate)?'-':user.birthdate)}
                  {listBoxItem('SSN', isUndefinedNullEmpty(user.work_kit.ssn)?'-': (user.work_kit.ssn.length <= 4?'XXX-XX-'+user.work_kit.ssn:user.work_kit.ssn))}
                  {listBoxItem('SAG number', isUndefinedNullEmpty(user.work_kit.sag_number)?'-': user.work_kit.sag_number)}
                  {user?.country && (user?.country.toLowerCase() == 'ca') && listBoxItem('ACTRA ID', isUndefinedNullEmpty(user.work_kit.actra_member_number)?'-': user.work_kit.actra_member_number)}
                </div>
              )}
            </div>
            <div className="flex  flex-col sm:flex-row gap-5" flex-row="">
              <h3 className="text-xl font-semibold mb-2" style={{ width: '10.625rem' }}>
                Loan-Out Information
              </h3>
              {!gridLayout && (
                <div className="flex flex-col gap-2-1 ">
                  {listBoxItem('Company', isUndefinedNullEmpty(user.work_kit.corporation)?'-': user.work_kit.corporation)}
                  {listBoxItem('Work Email', isUndefinedNullEmpty(user.work_kit.work_email)?'-': user.work_kit.work_email)}
                  {listBoxItem('Work Phone', isUndefinedNullEmpty(user.work_kit.work_phone)?'-': user.work_kit.work_phone)}
                  {listBoxItem('Address', getAddress(user.work_kit))}
                  {user?.country && (user?.country.toLowerCase() == 'ca') ? <>
                  {listBoxItem('GST Number', isUndefinedNullEmpty(user.work_kit.federal_tax_id)?'-': user.work_kit.federal_tax_id)}
                  {listBoxItem('HST Number', isUndefinedNullEmpty(user.work_kit.hst)?'-': user.work_kit.hst)}
                  </> : <>
                  {listBoxItem('Federal Tax ID', isUndefinedNullEmpty(user.work_kit.federal_tax_id)?'-': user.work_kit.federal_tax_id)}
                  </>}
                </div>
              )}
            </div>
          </div>

          {/* Right Section (Images) */}
          <div
            className="flex flex-col gap-1-1"
            style={{
              width: '20rem',
            }}
          >
            <div className="flex flex-col gap-4">
              <div className="text-xl font-semibold mb-2">Work Documents</div>
              <ul id="list_active" className="flex flex-col" style={{maxWidth:'18.75rem', width: '18.75rem', gap: '1rem'}}>

                {kitDocuments.map((itemkit, indexKit) => {
                  return <>{renderImage(itemkit, indexKit)}</>;
                })}
              </ul>
            </div>
          </div>
        </div>
      </div>{' '}
    </>
  );
};
